import React, { useState, useEffect } from 'react';
import {
  Box,
  Button,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  IconButton,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  TextField,
  Switch,
  FormControlLabel,
  CircularProgress,
  Chip,
  Select,
  MenuItem,
  FormControl,
  InputLabel,
} from '@mui/material';
import {
  Add as AddIcon,
  Edit as EditIcon,
  Delete as DeleteIcon,
} from '@mui/icons-material';
import { getFunctions, httpsCallable } from 'firebase/functions';
import { AreaMaster, RegionMaster, CountryMaster } from '../../types/master';

interface Props {
  onError: (message: string) => void;
  onSuccess: (message: string) => void;
}

const AreaMasterTab: React.FC<Props> = ({ onError, onSuccess }) => {
  const [areas, setAreas] = useState<AreaMaster[]>([]);
  const [regions, setRegions] = useState<RegionMaster[]>([]);
  const [countries, setCountries] = useState<CountryMaster[]>([]);
  const [loading, setLoading] = useState(false);
  const [dialogOpen, setDialogOpen] = useState(false);
  const [editingArea, setEditingArea] = useState<AreaMaster | null>(null);
  const [filterCountry, setFilterCountry] = useState<string>('');
  const [filterRegion, setFilterRegion] = useState<string>('');
  const [formData, setFormData] = useState({
    countryCode: '',
    regionId: '',
    code: '',
    nameJa: '',
    nameTh: '',
    nameEn: '',
    postalCodePattern: '',
    sortOrder: 0,
    isActive: true,
  });

  const functions = getFunctions();

  useEffect(() => {
    fetchCountries();
    fetchAreas();
  }, []);

  useEffect(() => {
    if (filterCountry) {
      fetchRegions(filterCountry);
      fetchAreas();
    }
  }, [filterCountry]);

  useEffect(() => {
    if (filterRegion) {
      fetchAreas();
    }
  }, [filterRegion]);

  const fetchCountries = async () => {
    try {
      const getMasterData = httpsCallable(functions, 'getMasterData');
      const result = await getMasterData({ type: 'countries' });
      const data = (result.data as any).data as CountryMaster[];
      setCountries(data);
    } catch (error: any) {
      onError(error.message || '国マスタの取得に失敗しました');
    }
  };

  const fetchRegions = async (countryCode?: string) => {
    try {
      const getMasterData = httpsCallable(functions, 'getMasterData');
      const params: any = { type: 'regions' };
      if (countryCode) {
        params.countryCode = countryCode;
      }
      const result = await getMasterData(params);
      const data = (result.data as any).data as RegionMaster[];
      setRegions(data);
    } catch (error: any) {
      onError(error.message || '地方マスタの取得に失敗しました');
    }
  };

  const fetchAreas = async () => {
    setLoading(true);
    try {
      const getMasterData = httpsCallable(functions, 'getMasterData');
      const params: any = { type: 'areas', includeInactive: true };
      if (filterCountry) {
        params.countryCode = filterCountry;
      }
      if (filterRegion) {
        params.regionId = filterRegion;
      }
      const result = await getMasterData(params);
      const data = (result.data as any).data as AreaMaster[];
      setAreas(data);
    } catch (error: any) {
      onError(error.message || 'エリアマスタの取得に失敗しました');
    } finally {
      setLoading(false);
    }
  };

  const handleOpenDialog = (area?: AreaMaster) => {
    if (area) {
      setEditingArea(area);
      setFormData({
        countryCode: area.countryCode,
        regionId: area.regionId,
        code: area.code,
        nameJa: area.name.ja,
        nameTh: area.name.th || '',
        nameEn: area.name.en || '',
        postalCodePattern: area.postalCodePattern || '',
        sortOrder: area.sortOrder,
        isActive: area.isActive,
      });
      // 地方リストを取得
      fetchRegions(area.countryCode);
    } else {
      setEditingArea(null);
      const defaultCountry = filterCountry || countries[0]?.code || '';
      setFormData({
        countryCode: defaultCountry,
        regionId: filterRegion || '',
        code: '',
        nameJa: '',
        nameTh: '',
        nameEn: '',
        postalCodePattern: '',
        sortOrder: areas.length + 1,
        isActive: true,
      });
      if (defaultCountry) {
        fetchRegions(defaultCountry);
      }
    }
    setDialogOpen(true);
  };

  const handleCloseDialog = () => {
    setDialogOpen(false);
    setEditingArea(null);
  };

  const handleCountryChange = (countryCode: string) => {
    setFormData({ ...formData, countryCode, regionId: '' });
    fetchRegions(countryCode);
  };

  const handleSave = async () => {
    try {
      if (!formData.countryCode || !formData.regionId || !formData.code || !formData.nameJa) {
        onError('必須項目を入力してください');
        return;
      }

      if (editingArea) {
        const updateMasterData = httpsCallable(functions, 'updateMasterData');
        await updateMasterData({
          id: editingArea.id,
          type: 'areas',
          data: {
            countryCode: formData.countryCode,
            regionId: formData.regionId,
            code: formData.code,
            name: {
              ja: formData.nameJa,
              th: formData.nameTh || undefined,
              en: formData.nameEn || undefined,
            },
            postalCodePattern: formData.postalCodePattern || undefined,
            sortOrder: formData.sortOrder,
            isActive: formData.isActive,
          },
        });
        onSuccess('エリアマスタを更新しました');
      } else {
        const createArea = httpsCallable(functions, 'createArea');
        await createArea({
          countryCode: formData.countryCode,
          regionId: formData.regionId,
          code: formData.code,
          name: {
            ja: formData.nameJa,
            th: formData.nameTh || undefined,
            en: formData.nameEn || undefined,
          },
          postalCodePattern: formData.postalCodePattern || undefined,
          sortOrder: formData.sortOrder,
          isActive: formData.isActive,
        });
        onSuccess('エリアマスタを作成しました');
      }

      handleCloseDialog();
      fetchAreas();
    } catch (error: any) {
      onError(error.message || '保存に失敗しました');
    }
  };

  const handleDelete = async (area: AreaMaster) => {
    if (!window.confirm(`${area.name.ja}を削除してもよろしいですか？`)) {
      return;
    }

    try {
      const deleteMasterData = httpsCallable(functions, 'deleteMasterData');
      const result = await deleteMasterData({
        id: area.id,
        type: 'areas',
      });
      onSuccess((result.data as any).message || 'エリアマスタを削除しました');
      fetchAreas();
    } catch (error: any) {
      onError(error.message || '削除に失敗しました');
    }
  };

  const getCountryName = (countryCode: string) => {
    return countries.find(c => c.code === countryCode)?.name.ja || countryCode;
  };

  const getRegionName = (regionId: string) => {
    return regions.find(r => r.id === regionId)?.name.ja || regionId;
  };

  const filteredRegions = regions.filter(r =>
    !filterCountry || r.countryCode === filterCountry
  );

  if (loading) {
    return (
      <Box display="flex" justifyContent="center" p={3}>
        <CircularProgress />
      </Box>
    );
  }

  return (
    <>
      <Box sx={{ mb: 2, display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
        <Box sx={{ display: 'flex', gap: 2, alignItems: 'center' }}>
          <Button
            variant="contained"
            startIcon={<AddIcon />}
            onClick={() => handleOpenDialog()}
          >
            エリアを追加
          </Button>

          <FormControl sx={{ minWidth: 150 }}>
            <InputLabel>国</InputLabel>
            <Select
              value={filterCountry}
              label="国"
              onChange={(e) => {
                setFilterCountry(e.target.value);
                setFilterRegion('');
              }}
            >
              <MenuItem value="">全て</MenuItem>
              {countries.map((country) => (
                <MenuItem key={country.code} value={country.code}>
                  {country.name.ja}
                </MenuItem>
              ))}
            </Select>
          </FormControl>

          <FormControl sx={{ minWidth: 200 }}>
            <InputLabel>地方</InputLabel>
            <Select
              value={filterRegion}
              label="地方"
              onChange={(e) => setFilterRegion(e.target.value)}
              disabled={!filterCountry}
            >
              <MenuItem value="">全て</MenuItem>
              {filteredRegions.map((region) => (
                <MenuItem key={region.id} value={region.id}>
                  {region.name.ja}
                </MenuItem>
              ))}
            </Select>
          </FormControl>
        </Box>
      </Box>

      <TableContainer>
        <Table>
          <TableHead>
            <TableRow>
              <TableCell>国</TableCell>
              <TableCell>地方</TableCell>
              <TableCell>コード</TableCell>
              <TableCell>名称（日本語）</TableCell>
              <TableCell>名称（タイ語）</TableCell>
              <TableCell>名称（英語）</TableCell>
              <TableCell>郵便番号パターン</TableCell>
              <TableCell>表示順</TableCell>
              <TableCell>状態</TableCell>
              <TableCell align="right">操作</TableCell>
            </TableRow>
          </TableHead>
          <TableBody>
            {areas.map((area) => (
              <TableRow key={area.id}>
                <TableCell>{getCountryName(area.countryCode)}</TableCell>
                <TableCell>{getRegionName(area.regionId)}</TableCell>
                <TableCell>{area.code}</TableCell>
                <TableCell>{area.name.ja}</TableCell>
                <TableCell>{area.name.th || '-'}</TableCell>
                <TableCell>{area.name.en || '-'}</TableCell>
                <TableCell>{area.postalCodePattern || '-'}</TableCell>
                <TableCell>{area.sortOrder}</TableCell>
                <TableCell>
                  <Chip
                    label={area.isActive ? '有効' : '無効'}
                    color={area.isActive ? 'success' : 'default'}
                    size="small"
                  />
                </TableCell>
                <TableCell align="right">
                  <IconButton size="small" onClick={() => handleOpenDialog(area)}>
                    <EditIcon />
                  </IconButton>
                  <IconButton size="small" onClick={() => handleDelete(area)}>
                    <DeleteIcon />
                  </IconButton>
                </TableCell>
              </TableRow>
            ))}
          </TableBody>
        </Table>
      </TableContainer>

      <Dialog open={dialogOpen} onClose={handleCloseDialog} maxWidth="md" fullWidth>
        <DialogTitle>
          {editingArea ? 'エリアマスタを編集' : 'エリアマスタを追加'}
        </DialogTitle>
        <DialogContent>
          <FormControl fullWidth margin="normal" required>
            <InputLabel>国</InputLabel>
            <Select
              value={formData.countryCode}
              label="国"
              onChange={(e) => handleCountryChange(e.target.value)}
              disabled={!!editingArea}
            >
              {countries.map((country) => (
                <MenuItem key={country.code} value={country.code}>
                  {country.name.ja}
                </MenuItem>
              ))}
            </Select>
          </FormControl>

          <FormControl fullWidth margin="normal" required>
            <InputLabel>地方</InputLabel>
            <Select
              value={formData.regionId}
              label="地方"
              onChange={(e) => setFormData({ ...formData, regionId: e.target.value })}
              disabled={!formData.countryCode || !!editingArea}
            >
              {regions.map((region) => (
                <MenuItem key={region.id} value={region.id}>
                  {region.name.ja}
                </MenuItem>
              ))}
            </Select>
          </FormControl>

          <TextField
            fullWidth
            label="コード"
            value={formData.code}
            onChange={(e) => setFormData({ ...formData, code: e.target.value })}
            margin="normal"
            required
            disabled={!!editingArea}
          />
          <TextField
            fullWidth
            label="名称（日本語）"
            value={formData.nameJa}
            onChange={(e) => setFormData({ ...formData, nameJa: e.target.value })}
            margin="normal"
            required
          />
          <TextField
            fullWidth
            label="名称（タイ語）"
            value={formData.nameTh}
            onChange={(e) => setFormData({ ...formData, nameTh: e.target.value })}
            margin="normal"
          />
          <TextField
            fullWidth
            label="名称（英語）"
            value={formData.nameEn}
            onChange={(e) => setFormData({ ...formData, nameEn: e.target.value })}
            margin="normal"
          />
          <TextField
            fullWidth
            label="郵便番号パターン（正規表現）"
            value={formData.postalCodePattern}
            onChange={(e) => setFormData({ ...formData, postalCodePattern: e.target.value })}
            margin="normal"
            placeholder="^1[0-9]{2}"
            helperText="例: 東京都の場合 ^1[0-9]{2}"
          />
          <TextField
            fullWidth
            label="表示順"
            type="number"
            value={formData.sortOrder}
            onChange={(e) => setFormData({ ...formData, sortOrder: parseInt(e.target.value) })}
            margin="normal"
          />
          <FormControlLabel
            control={
              <Switch
                checked={formData.isActive}
                onChange={(e) => setFormData({ ...formData, isActive: e.target.checked })}
              />
            }
            label="有効"
          />
        </DialogContent>
        <DialogActions>
          <Button onClick={handleCloseDialog}>キャンセル</Button>
          <Button onClick={handleSave} variant="contained">
            保存
          </Button>
        </DialogActions>
      </Dialog>
    </>
  );
};

export default AreaMasterTab;
